// src/features/auth/ForgotPassword.tsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, TextField, Typography, Link as MuiLink, Alert } from "@mui/material";
import { Link } from "react-router-dom";
import api from "../../services/api";

interface FormData {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    try {
      setError(null);
      await api.post("/users/password/request-reset", { email: data.email });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send reset email");
    }
  };

  if (submitted) {
    return (
      <Box maxWidth={500} mx="auto" mt={8}>
        <Alert severity="success" sx={{ mb: 2 }}>
          If an account exists with this email, a password reset link has been sent.
          Please check your email inbox.
        </Alert>
        <Typography variant="body2" align="center">
          <MuiLink component={Link} to="/login">Back to login</MuiLink>
        </Typography>
      </Box>
    );
  }

  return (
    <Box maxWidth={400} mx="auto" mt={8}>
      <Typography variant="h4" gutterBottom>
        Forgot Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your email address and we'll send you a link to reset your password.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          label="Email"
          fullWidth
          margin="normal"
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address"
            }
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={isSubmitting}>
          {isSubmitting ? "Sending..." : "Send Reset Link"}
        </Button>
      </form>

      <Typography variant="body2" align="center" sx={{ mt: 2 }}>
        Remember your password? <MuiLink component={Link} to="/login">Log in</MuiLink>
      </Typography>
    </Box>
  );
};

export default ForgotPassword;
